import React, { useEffect, useRef } from 'react';
import './PixelEffect.css';

const PixelEffect = () => {
    const canvasRef = useRef(null);
    const animationFrameRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', { alpha: true });

        const isMobile = window.innerWidth <= 768;
        const PARTICLE_COUNT = isMobile ? 1200 : 4000;
        const MOUSE_RADIUS = isMobile ? 60 : 120;
        const MOUSE_FORCE = 6;
        const FRICTION = 0.93;
        const RETURN_SPEED = 0.008;

        // Bright luminous green palette — visible on dark background
        const COLORS = [
            [30, 180, 80],     // Vivid green
            [50, 205, 50],     // Lime green
            [16, 185, 129],    // Emerald
            [34, 220, 100],    // Bright forest
            [80, 255, 120],    // Neon green
            [52, 211, 153],    // Mint
            [110, 255, 170],   // Light neon
            [144, 238, 144],   // Pale green
            [180, 255, 200],   // Soft glow green
            [200, 255, 220],   // Near-white green
        ];

        // Typed arrays for particle data
        let homeX, homeY, posX, posY, velX, velY;
        let sizes, cr, cg, cb, baseOpacity, opacity;
        let driftPhase, driftSpeedX, driftSpeedY, twinklePhase, twinkleSpeed;
        let count = 0;
        let time = 0;

        const initParticles = () => {
            const w = canvas.width;
            const h = canvas.height;
            count = PARTICLE_COUNT;

            homeX = new Float32Array(count);
            homeY = new Float32Array(count);
            posX = new Float32Array(count);
            posY = new Float32Array(count);
            velX = new Float32Array(count);
            velY = new Float32Array(count);
            sizes = new Float32Array(count);
            cr = new Uint8Array(count);
            cg = new Uint8Array(count);
            cb = new Uint8Array(count);
            baseOpacity = new Float32Array(count);
            opacity = new Float32Array(count);
            driftPhase = new Float32Array(count);
            driftSpeedX = new Float32Array(count);
            driftSpeedY = new Float32Array(count);
            twinklePhase = new Float32Array(count);
            twinkleSpeed = new Float32Array(count);

            for (let i = 0; i < count; i++) {
                const depth = Math.random();
                sizes[i] = 1.5 + depth * (isMobile ? 3 : 4.5) + Math.random() * 2;

                const col = COLORS[Math.floor(Math.random() * COLORS.length)];
                cr[i] = col[0];
                cg[i] = col[1];
                cb[i] = col[2];

                homeX[i] = Math.random() * w;
                // All particles start below viewport — revealed by scrolling
                homeY[i] = h + Math.random() * h * 4;
                posX[i] = homeX[i];
                posY[i] = homeY[i];
                velX[i] = 0;
                velY[i] = 0;

                baseOpacity[i] = 0.5 + depth * 0.4;
                opacity[i] = 0;

                driftPhase[i] = Math.random() * 6.28;
                driftSpeedX[i] = (Math.random() - 0.5) * 0.3;
                driftSpeedY[i] = 0.15 + Math.random() * 0.4;
                twinklePhase[i] = Math.random() * 6.28;
                twinkleSpeed[i] = 0.5 + Math.random() * 1.5;
            }
        };

        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            initParticles();
        };

        let mx = -9999, my = -9999, mvx = 0, mvy = 0;
        const handleMouseMove = (e) => {
            mvx = e.clientX - mx;
            mvy = e.clientY - my;
            mx = e.clientX;
            my = e.clientY;
        };

        let scrollY = 0;
        const handleScroll = () => { scrollY = window.pageYOffset; };

        const animate = () => {
            time += 0.016;
            const w = canvas.width;
            const h = canvas.height;
            ctx.clearRect(0, 0, w, h);

            const maxScroll = document.body.scrollHeight - h;
            const scrollProgress = maxScroll > 0 ? Math.min(scrollY / maxScroll, 1) : 0;
            const currentSection = scrollProgress * 8;

            // Fade out during transition
            let globalAlpha = 1;
            if (currentSection >= 0.45 && currentSection < 0.75) {
                globalAlpha = Math.max(0, 1 - ((currentSection - 0.45) / 0.3) * 1.5);
            } else if (currentSection >= 0.75) {
                globalAlpha = 0;
            }

            if (globalAlpha <= 0.01) {
                animationFrameRef.current = requestAnimationFrame(animate);
                return;
            }

            // Scroll lifts particles from bottom into view
            const scrollLift = scrollProgress * h * 8;

            const mouseSpeed = Math.abs(mvx) + Math.abs(mvy);
            const dynForce = MOUSE_FORCE + mouseSpeed * 0.2;
            const dynRadius = MOUSE_RADIUS + mouseSpeed;
            const dynRadiusSq = dynRadius * dynRadius;

            ctx.globalCompositeOperation = 'lighter';

            for (let i = 0; i < count; i++) {
                // Target position — particles rise from bottom on scroll
                const scrolledY = homeY[i] - scrollLift;
                const tX = homeX[i] + Math.sin(time * driftSpeedY[i] + driftPhase[i]) * driftSpeedX[i] * 2;
                const tY = scrolledY - Math.cos(time * driftSpeedX[i] * 0.5 + driftPhase[i]) * 0.3;

                // Quick bounds check
                if (tY < -60 || tY > h + 60) {
                    opacity[i] *= 0.92;
                    continue;
                }

                // Smooth edge fade (bottom entry)
                let ef = 1;
                if (tY > h - 60) ef = (h + 60 - tY) / 120;
                else if (tY < 60) ef = (tY + 60) / 120;
                if (ef > 1) ef = 1; else if (ef < 0) ef = 0;

                // Twinkle
                twinklePhase[i] += twinkleSpeed[i] * 0.016;
                const tw = 0.7 + Math.sin(twinklePhase[i]) * 0.3;

                // Smooth opacity lerp
                const targetOp = baseOpacity[i] * ef * globalAlpha * tw;
                opacity[i] += (targetOp - opacity[i]) * 0.06;

                if (opacity[i] < 0.01) continue;

                // Mouse interaction
                const dx = posX[i] - mx;
                const dy = posY[i] - my;
                const distSq = dx * dx + dy * dy;

                if (distSq < dynRadiusSq && distSq > 1) {
                    const dist = Math.sqrt(distSq);
                    const force = (1 - dist / dynRadius) * dynForce * 0.3;
                    const invDist = 1 / dist;
                    velX[i] += dx * invDist * force + mvx * 0.04 * (1 - dist / dynRadius);
                    velY[i] += dy * invDist * force + mvy * 0.04 * (1 - dist / dynRadius);
                }

                // Physics
                velX[i] *= FRICTION;
                velY[i] *= FRICTION;
                velX[i] += (tX - posX[i]) * RETURN_SPEED;
                velY[i] += (tY - posY[i]) * RETURN_SPEED;
                posX[i] += velX[i];
                posY[i] += velY[i];

                // Draw — filled circle with soft radial gradient for glow
                const radius = sizes[i] * 0.5;
                ctx.globalAlpha = opacity[i];
                const grd = ctx.createRadialGradient(posX[i], posY[i], 0, posX[i], posY[i], radius * 2.5);
                grd.addColorStop(0, `rgba(${cr[i]},${cg[i]},${cb[i]},1)`);
                grd.addColorStop(0.4, `rgba(${cr[i]},${cg[i]},${cb[i]},0.4)`);
                grd.addColorStop(1, `rgba(${cr[i]},${cg[i]},${cb[i]},0)`);
                ctx.fillStyle = grd;
                ctx.beginPath();
                ctx.arc(posX[i], posY[i], radius * 2.5, 0, 6.28);
                ctx.fill();
            }

            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha = 1;

            // Decay mouse velocity
            mvx *= 0.85;
            mvy *= 0.85;

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        resizeCanvas();
        animate();

        window.addEventListener('resize', resizeCanvas);
        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            cancelAnimationFrame(animationFrameRef.current);
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return <canvas ref={canvasRef} className="pixel-effect-canvas" />;
};

export default PixelEffect;
